import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import ls from 'local-storage';

import { UserRoles } from '../../constants/roles';
import { selectConfig } from '../global/globalSlice';
import { getNotaryByEmail } from '../ipenNotary/ipenNotaryAPI';
import { ConfigIProps } from '../orders/ordersTypes';
import { RootState } from '../store';
import {
  beginAuth,
  getOffices,
  getToken,
  startLogout,
  verifyAuth,
} from './authAPI';
import {
  IAuthState,
  IAuthToken,
  IBeginAuthRequest,
  IVerifyAuthRequest,
} from './authTypes';

export const initialState: IAuthState = {
  currentUser: {
    user_roles: '{}',
  },
  userOffices: {},
  isOfficesLoaded: false,
  isLoggedIn: false,
  loading: false,
  message: null,
  isThirdParty: false,
  isNotary: false,
  isNotaryRoleOnly: false,
  isAdmin: false,
  isEoea: false,
  isTPEoea: false,
  isGuest: false,
  challenge: {},
};

// The function below is called a thunk and allows us to perform async logic. It
// can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
// will call the thunk with the `dispatch` function as the first argument. Async
// code can then be executed and other actions can be dispatched. Thunks are
// typically used to make async requests.
export const getTokenAsync = createAsyncThunk(
  'auth/getToken',
  async (data: IAuthToken, { getState, rejectWithValue }) => {
    const config: any = selectConfig(getState() as any);
    let officesRes = {};
    try {
      const tokenRes: any = await getToken(data, config);
      const roleJson = JSON.parse(tokenRes['user_roles'].toString());
      const isNotary = Object.prototype.hasOwnProperty.call(
        roleJson,
        UserRoles.NOTARY_USERS_ROLE
      );
      const isAdmin = Object.prototype.hasOwnProperty.call(
        roleJson,
        UserRoles.GLOBAL_USERS_ROLE
      );
      const isEoea = Object.prototype.hasOwnProperty.call(
        roleJson,
        UserRoles.EOEA_USERS_ROLE
      );

      const isNotaryRoleOnly = isNotary && Object.keys(roleJson)?.length === 1;
      if (isNotary) {
        await getNotaryByEmail(tokenRes.email, config).then((notaryDetails) => {
          if (notaryDetails) {
            ls(
              'CommissionExpirationDate',
              notaryDetails.CommissionExpirationDate
            );
            ls('CommissionStatus', notaryDetails.Status);
          } else {
            ls('CommissionExpirationDate', null);
            ls('CommissionStatus', null);
          }
        });
      }
      if (tokenRes) {
        officesRes = await getOffices(tokenRes, config);
      }
      return {
        tokenRes: tokenRes,
        offices: officesRes,
        isNotary: isNotary,
        isNotaryRoleOnly: isNotaryRoleOnly,
        isAdmin: isAdmin,
        isEoea: isEoea,
      };
    } catch (message: any) {
      return rejectWithValue(message);
    }
  }
);

export const startLogoutAsync = createAsyncThunk(
  'auth/startLogout',
  async (isDenied: boolean, { getState }) => {
    const config: any = selectConfig(getState() as any);
    const response = await startLogout(isDenied, config);
    return response;
  }
);

export const getOfficesAsync = createAsyncThunk(
  'auth/getOffices',
  async (_, { getState, rejectWithValue }) => {
    const config: ConfigIProps = selectConfig(getState() as any);
    const currentUser: any = selectCurrentUser(getState() as any);
    try {
      const response = await getOffices(currentUser, config);
      return response;
    } catch (message: any) {
      return rejectWithValue(message);
    }
  }
);

export const beginAuthAsync = createAsyncThunk(
  'auth/beginAuth',
  async (data: IBeginAuthRequest, { getState, rejectWithValue }) => {
    const config: ConfigIProps = selectConfig(getState() as any);
    try {
      const response: any = await beginAuth(data, config);
      return response.data;
    } catch (message: any) {
      return rejectWithValue(message);
    }
  }
);

export const verifyAuthAsync = createAsyncThunk(
  'auth/verifyAuth',
  async (data: IVerifyAuthRequest, { getState, rejectWithValue }) => {
    const config: ConfigIProps = selectConfig(getState() as any);
    try {
      const response: any = await verifyAuth(data, config);
      if (response.data?.Session == null) {
        // if url redirect is to okta docutech
        if (response.data?.Url && response.data.Url.includes('docutech')) {
          return response.data;
        } else {
          const roleJson = JSON.parse(response['user_roles'].toString());
          const isGuest = Object.keys(roleJson)?.includes('Guest_Users');
          return {
            tokenRes: response,
            offices: {},
            isNotary: false,
            isNotaryRoleOnly: false,
            isAdmin: false,
            isEoea: false,
            isGuest: isGuest,
          };
        }
      }
      return response.data;
    } catch (message: any) {
      return rejectWithValue(message);
    }
  }
);

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    loginSuccess: (state: any, action: PayloadAction<any>) => {
      state.currentUser = action.payload;
      state.isLoggedIn = true;
      state.loading = false;
      state.isThirdParty = !action.payload.tenant_id?.includes('FIAM');
      state.message = null;
    },
    setUserData: (
      state: any,
      action: PayloadAction<Record<string, unknown>>
    ) => {
      state.userOffices = action.payload;
      state.isOfficesLoaded = true;
      state.isLoggedIn = true;
      state.loading = false;
      state.message = null;
    },
    logOut: (state: any) => {
      state.currentUser = {};
      state.isLoggedIn = false;
      state.loading = false;
      state.message = null;
    },
    clearAuthMessage: (state: any) => {
      state.message = null;
    },
    setRoles: (state: any) => {
      const roleJson = JSON.parse(
        state?.currentUser?.['user_roles'].toString()
      );
      state.isNotary = Object.prototype.hasOwnProperty.call(
        roleJson,
        UserRoles.NOTARY_USERS_ROLE
      );
      state.isAdmin = Object.prototype.hasOwnProperty.call(
        roleJson,
        UserRoles.GLOBAL_USERS_ROLE
      );
      state.isEoea = Object.prototype.hasOwnProperty.call(
        roleJson,
        UserRoles.EOEA_USERS_ROLE
      );
      state.isGuest = Object.prototype.hasOwnProperty.call(
        roleJson,
        UserRoles.GUEST_USERS_ROLE
      );

      const roleCount = Object.keys(roleJson)?.length;
      state.isNotaryRoleOnly =
        Object.keys(roleJson)?.includes(UserRoles.NOTARY_USERS_ROLE) &&
        roleCount === 1;
      state.isTPEoea = state.isEoea && state.isThirdParty;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTokenAsync.pending, (state) => {
        state.loading = true;
        state.message = null;
      })
      .addCase(getTokenAsync.fulfilled, (state: any, action: any) => {
        state.currentUser = action.payload.tokenRes;
        state.userOffices = action.payload.offices;
        state.isThirdParty =
          !action.payload.tokenRes.tenant_id?.includes('FIAM');
        state.isOfficesLoaded = true;
        state.isLoggedIn = true;
        state.loading = false;
        state.message = null;
        state.isNotary = action.payload.isNotary;
        state.isNotaryRoleOnly = action.payload.isNotaryRoleOnly;
        state.isAdmin = action.payload.isAdmin;
        state.isEoea = action.payload.isEoea;
        state.isTPEoea =
          !action.payload.tokenRes.tenant_id?.includes('FIAM') &&
          action.payload.isEoea;
      })
      .addCase(getTokenAsync.rejected, (state: any, action: any) => {
        state.currentUser = {};
        state.isLoggedIn = false;
        state.loading = false;
        state.message = { type: 'error', text: action.payload };
      })
      .addCase(verifyAuthAsync.pending, (state) => {
        state.message = null;
      })
      .addCase(verifyAuthAsync.fulfilled, (state: any, action: any) => {
        if (!action.payload?.Session) {
          if (
            !(action.payload.Url && action.payload.Url.includes('docutech'))
          ) {
            state.redirectURL = action.payload.url;
            state.currentUser = action.payload.tokenRes;
            state.userOffices = action.payload.offices;
            state.isThirdParty =
              !action.payload.tokenRes.tenant_id?.includes('FIAM');
            state.isOfficesLoaded = true;
            state.isLoggedIn = true;
            state.message = null;
            state.isNotary = action.payload.isNotary;
            state.isNotaryRoleOnly = action.payload.isNotaryRoleOnly;
            state.isAdmin = action.payload.isAdmin;
            state.isGuest = action.payload.isGuest;
            state.isTPEoea =
              !action.payload.tokenRes.tenant_id?.includes('FIAM') &&
              action.payload.isEoea;
            state.session = null;
            state.challenge = null;
          }
        } else {
          if (action.payload.HasFailedChallenge) {
            state.message = {
              type: 'error',
              message: 'Incorrect credentials, try again.',
            };
          } else {
            state.message = null;
          }
          state.session = action.payload.Session;
          state.challenge = action.payload.Challenge;
        }
        state.loading = false;
      })
      .addCase(verifyAuthAsync.rejected, (state: any, action: any) => {
        state.isLoggedIn = false;
        state.loading = false;
        state.message = { type: 'error', message: action.payload };
      })
      .addCase(startLogoutAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(startLogoutAsync.fulfilled, (state: any, action: any) => {
        state.currentUser = {};
        state.isLoggedIn = false;
        state.loading = false;
        state.message = action.payload;
      })
      .addCase(startLogoutAsync.rejected, (state: any, action: any) => {
        state.currentUser = {};
        state.isLoggedIn = false;
        state.loading = false;
        state.message = action.payload;
      })
      .addCase(getOfficesAsync.pending, (state) => {
        state.loading = true;
        state.message = null;
      })
      .addCase(getOfficesAsync.fulfilled, (state: any, action: any) => {
        state.userOffices = action.payload;
        state.loading = false;
        state.message = null;
      })
      .addCase(getOfficesAsync.rejected, (state) => {
        state.loading = false;
        state.message = {
          type: 'error',
          message: 'oopsie',
        };
      })
      .addCase(beginAuthAsync.pending, (state) => {
        state.message = null;
      })
      .addCase(beginAuthAsync.fulfilled, (state: any, action: any) => {
        state.session = action.payload.Session;
        state.challenge = action.payload.Challenge;
        state.message = null;
      })
      .addCase(beginAuthAsync.rejected, (state) => {
        state.message = {
          type: 'error',
          message: 'There was an error getting authentication challenge',
        };
      });
  },
});

export const { setUserData, loginSuccess, logOut, clearAuthMessage, setRoles } =
  authSlice.actions;
export const selectOffices = (state: RootState) => state.auth.userOffices;
export const selectCurrentUser = (state: RootState) => state.auth.currentUser;

export default authSlice.reducer;
